import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Spinner,
  useToast,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Link,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import {
  FiSave,
  FiRefreshCw,
  FiUpload,
  FiExternalLink,
  FiLock,
  FiUnlock,
  FiEdit3,
} from 'react-icons/fi';
import { canvasAPI, api } from '../../../services/api';
import SignatureCanvasComponent from '../SignatureCanvas';

const InstitutionSettingsTab = ({ settings, loading, onRefresh }) => {
  const [formData, setFormData] = useState({
    institution_name: '',
    director_name: '',
    director_title: '',
    default_duration: '40 horas',
    institution_primary_color: '#202D5C',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [loadingOauthStatus, setLoadingOauthStatus] = useState(false);
  const [signatureTimestamp, setSignatureTimestamp] = useState(Date.now());
  const { isOpen: isCanvasOpen, onOpen: onCanvasOpen, onClose: onCanvasClose } = useDisclosure();
  const fileInputRef = React.useRef();
  const logoInputRef = React.useRef();
  const toast = useToast();

  useEffect(() => {
    if (settings) {
      setFormData({
        institution_name: settings.institution_name || '',
        director_name: settings.director_name || '',
        director_title: settings.director_title || '',
        default_duration: settings.default_duration || '40 horas',
        institution_primary_color: settings.institution_primary_color || '#202D5C',
      });
    }
    
    // Load OAuth status
    loadOAuthStatus();
  }, [settings]);
  
  const loadOAuthStatus = async () => {
    try {
      setLoadingOauthStatus(true);
      const response = await api.get('/canvas/oauth/status');
      setOauthStatus(response.data);
    } catch (error) {
      console.error('Error loading OAuth status:', error);
    } finally {
      setLoadingOauthStatus(false);
    }
  };

  const handleAuthorizeOAuth = async () => {
    try {
      const response = await api.get('/canvas/oauth/authorize');
      const authUrl = response.data.authorization_url;
      
      // Open authorization URL in new window
      const popup = window.open(authUrl, '_blank', 'width=600,height=800');
      
      // Listen for messages from popup
      const messageHandler = (event) => {
        if (event.data && event.data.type === 'oauth_success') {
          toast({
            title: 'Autorización completada',
            description: 'Acceso a Canvas API configurado exitosamente',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          loadOAuthStatus();
          window.removeEventListener('message', messageHandler);
        } else if (event.data && event.data.type === 'oauth_error') {
          toast({
            title: 'Error en autorización',
            description: event.data.error || 'Error desconocido',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          window.removeEventListener('message', messageHandler);
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      toast({
        title: 'Autorización iniciada',
        description: 'Complete la autorización en la ventana de Canvas',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo iniciar la autorización',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRevokeOAuth = async () => {
    if (!window.confirm('¿Está seguro de que desea revocar el acceso a Canvas API? Las funciones que requieren datos de Canvas dejarán de funcionar.')) {
      return;
    }
    
    try {
      await api.post('/canvas/oauth/revoke');
      
      toast({
        title: 'Acceso revocado',
        description: 'El token de OAuth2 ha sido revocado exitosamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      loadOAuthStatus();
    } catch (error) {
      console.error('Error revoking OAuth:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo revocar el acceso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      await canvasAPI.updateInstitutionSettings(formData);

      toast({
        title: 'Configuración actualizada',
        description: 'Los cambios se guardaron exitosamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo guardar la configuración',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleLogoUploadClick = () => {
    logoInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      await canvasAPI.uploadDirectorSignature(file);

      toast({
        title: 'Firma actualizada',
        description: 'La firma del director se ha subido correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setSignatureTimestamp(Date.now());
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo subir la firma',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDirectorSignatureSave = async (file) => {
    try {
      setIsUploading(true);
      await canvasAPI.uploadDirectorSignature(file);

      toast({
        title: 'Firma dibujada guardada',
        description: 'La firma del director se ha subido correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setSignatureTimestamp(Date.now());
      onCanvasClose();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo subir la firma',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCanvasCancel = () => {
    onCanvasClose();
  };

  const handleLogoFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsUploadingLogo(true);
      await canvasAPI.uploadInstitutionLogo(file);

      toast({
        title: 'Logo actualizado',
        description: 'El logo institucional se ha subido correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo subir el logo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando configuración...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardHeader>
          <HStack justify="space-between">
            <Box>
              <Heading size="md">Configuración Institucional</Heading>
              <Text fontSize="sm" color="gray.600" mt={1}>
                Estos datos aparecerán en todos los certificados emitidos
              </Text>
            </Box>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={onRefresh}
              variant="ghost"
              size="sm"
            >
              Recargar
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Institution Info */}
            <Box>
              <Heading size="sm" mb={4}>
                Información Institucional
              </Heading>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Nombre de la Institución</FormLabel>
                  <Input
                    value={formData.institution_name}
                    onChange={(e) => handleChange('institution_name', e.target.value)}
                    placeholder="Ej: Universidad de Ejemplo"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Nombre del Director</FormLabel>
                  <Input
                    value={formData.director_name}
                    onChange={(e) => handleChange('director_name', e.target.value)}
                    placeholder="Ej: Dr. Juan Pérez"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Cargo del Director</FormLabel>
                  <Input
                    value={formData.director_title}
                    onChange={(e) => handleChange('director_title', e.target.value)}
                    placeholder="Ej: Director General"
                  />
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Default Settings */}
            <Box>
              <Heading size="sm" mb={4}>
                Configuración por Defecto
              </Heading>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Duración Predeterminada</FormLabel>
                  <Input
                    value={formData.default_duration}
                    onChange={(e) => handleChange('default_duration', e.target.value)}
                    placeholder="Ej: 40 horas"
                  />
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Esta duración se usará por defecto al crear certificados
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Color Institucional</FormLabel>
                  <HStack>
                    <Input
                      type="color"
                      value={formData.institution_primary_color}
                      onChange={(e) => handleChange('institution_primary_color', e.target.value)}
                      w="80px"
                      h="40px"
                    />
                    <Input
                      value={formData.institution_primary_color}
                      onChange={(e) => handleChange('institution_primary_color', e.target.value)}
                      placeholder="#202D5C"
                      maxW="150px"
                    />
                    <Box
                      w="40px"
                      h="40px"
                      bg={formData.institution_primary_color}
                      borderRadius="md"
                      borderWidth="1px"
                    />
                  </HStack>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Este color se usa en los bordes y elementos del certificado
                  </Text>
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Current Files Info */}
            {settings && (
              <Box>
                <Heading size="sm" mb={4}>
                  Archivos y Firmas
                </Heading>
                <VStack spacing={4} align="stretch">
                  <Box p={4} borderWidth="1px" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="medium">Firma del Director</Text>
                      <Badge colorScheme={settings.director_signature_path ? 'green' : 'gray'}>
                        {settings.director_signature_path ? 'Configurada' : 'No configurada'}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Esta firma aparecerá automáticamente en todos los certificados generados.
                    </Text>
                    <HStack spacing={2}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept="image/*"
                      />
                      <Button
                        leftIcon={<FiUpload />}
                        size="sm"
                        onClick={handleUploadClick}
                        isLoading={isUploading}
                        loadingText="Subiendo..."
                      >
                        Subir Archivo
                      </Button>
                      <Button
                        leftIcon={<FiEdit3 />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={onCanvasOpen}
                        isDisabled={isUploading}
                      >
                        Dibujar Firma
                      </Button>
                    </HStack>
                    {settings.director_signature_path && (
                      <VStack align="start" mt={3} spacing={2}>
                        <Box
                          w="150px"
                          h="100px"
                          borderWidth="1px"
                          borderRadius="md"
                          overflow="hidden"
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderColor="gray.200"
                        >
                          <img
                            src={`${canvasAPI.getImageThumbnailUrl('Firma_Director.png')}?t=${signatureTimestamp}`}
                            alt="Firma del Director"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<Text fontSize="xs" color="gray.400">Vista previa no disponible</Text>';
                            }}
                          />
                        </Box>
                        <Text fontSize="xs" color="green.600">
                          Archivo actual: {settings.director_signature_path.split('/').pop()}
                        </Text>
                      </VStack>
                    )}
                  </Box>

                  <Box p={4} borderWidth="1px" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="medium">Logo Institucional</Text>
                      <Badge colorScheme={settings.logo_path ? 'green' : 'gray'}>
                        {settings.logo_path ? 'Configurado' : 'No configurado'}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Este logo aparecerá en el encabezado de los certificados generados.
                    </Text>
                    <HStack>
                      <input
                        type="file"
                        ref={logoInputRef}
                        onChange={handleLogoFileChange}
                        style={{ display: 'none' }}
                        accept="image/*"
                      />
                      <Button
                        leftIcon={<FiUpload />}
                        size="sm"
                        onClick={handleLogoUploadClick}
                        isLoading={isUploadingLogo}
                        loadingText="Subiendo..."
                      >
                        Subir Nuevo Logo
                      </Button>
                    </HStack>
                    {settings.logo_path && (
                      <VStack align="start" mt={3} spacing={2}>
                        <Box
                          w="150px"
                          h="100px"
                          borderWidth="1px"
                          borderRadius="md"
                          overflow="hidden"
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderColor="gray.200"
                        >
                          <img
                            src={canvasAPI.getImageThumbnailUrl('institution_logo.png')}
                            alt="Logo Institucional"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<Text fontSize="xs" color="gray.400">Vista previa no disponible</Text>';
                            }}
                          />
                        </Box>
                        <Text fontSize="xs" color="green.600">
                          Archivo actual: {settings.logo_path.split('/').pop()}
                        </Text>
                      </VStack>
                    )}
                  </Box>
                </VStack>
              </Box>
            )}

            <Divider />

            {/* Canvas API OAuth2 Access */}
            <Box>
              <Heading size="sm" mb={4}>
                Acceso a Canvas API (OAuth2)
              </Heading>
              
              {loadingOauthStatus ? (
                <Box textAlign="center" py={4}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.600" mt={2}>Verificando estado...</Text>
                </Box>
              ) : (
                <VStack spacing={4} align="stretch">
                  <Alert
                    status={oauthStatus?.oauth_configured ? 'success' : 'warning'}
                    variant="left-accent"
                  >
                    <AlertIcon />
                    <Box flex="1">
                      <AlertTitle>
                        {oauthStatus?.oauth_configured ? 'OAuth2 Configurado' : 'OAuth2 No Configurado'}
                      </AlertTitle>
                      <AlertDescription fontSize="sm">
                        {oauthStatus?.oauth_configured ? (
                          <>
                            {oauthStatus?.using_oauth && (
                              <Text>
                                Usando token OAuth2. 
                                {oauthStatus?.token_valid ? (
                                  <Badge ml={2} colorScheme="green">Válido</Badge>
                                ) : (
                                  <Badge ml={2} colorScheme="red">Expirado</Badge>
                                )}
                                {oauthStatus?.token_expires_at && (
                                  <Text fontSize="xs" mt={1}>
                                    Expira: {new Date(oauthStatus.token_expires_at).toLocaleString()}
                                  </Text>
                                )}
                              </Text>
                            )}
                            {oauthStatus?.using_static_token && !oauthStatus?.using_oauth && (
                              <Text>
                                Usando token estático (legacy). 
                                <Badge ml={2} colorScheme="yellow">Recomendamos migrar a OAuth2</Badge>
                              </Text>
                            )}
                          </>
                        ) : (
                          'La aplicación necesita autorización para acceder a Canvas API y obtener datos de estudiantes y calificaciones.'
                        )}
                      </AlertDescription>
                    </Box>
                  </Alert>

                  <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <VStack align="stretch" spacing={3}>
                      <Text fontSize="sm" fontWeight="medium">
                        ¿Qué es OAuth2?
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        OAuth2 es un método seguro de autenticación que permite a esta aplicación:
                      </Text>
                      <VStack align="start" spacing={1} pl={4} fontSize="sm">
                        <Text>• Obtener lista de estudiantes matriculados</Text>
                        <Text>• Leer calificaciones y notas del curso</Text>
                        <Text>• Acceder a información de assignments</Text>
                        <Text>• Todo sin compartir contraseñas</Text>
                      </VStack>
                      <Text fontSize="sm" color="gray.600" fontStyle="italic">
                        Los tokens se refrescan automáticamente cada hora. Los permisos son solo de lectura.
                      </Text>
                    </VStack>
                  </Box>

                  <HStack spacing={3}>
                    {!oauthStatus?.oauth_configured || !oauthStatus?.using_oauth ? (
                      <Button
                        leftIcon={<FiLock />}
                        colorScheme="blue"
                        onClick={handleAuthorizeOAuth}
                        size="md"
                      >
                        Autorizar Acceso a Canvas API
                      </Button>
                    ) : (
                      <>
                        <Button
                          leftIcon={<FiRefreshCw />}
                          variant="outline"
                          onClick={loadOAuthStatus}
                          size="sm"
                        >
                          Actualizar Estado
                        </Button>
                        <Button
                          leftIcon={<FiUnlock />}
                          colorScheme="red"
                          variant="outline"
                          onClick={handleRevokeOAuth}
                          size="sm"
                        >
                          Revocar Acceso
                        </Button>
                      </>
                    )}
                    <Link
                      href="https://canvas.instructure.com/doc/api/file.oauth.html"
                      isExternal
                      fontSize="sm"
                      color="blue.600"
                    >
                      Documentación OAuth2 <FiExternalLink style={{ display: 'inline' }} />
                    </Link>
                  </HStack>
                </VStack>
              )}
            </Box>

            {/* Save Button */}
            <HStack justify="flex-end" pt={4}>
              <Button
                leftIcon={<FiSave />}
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSaving}
                loadingText="Guardando..."
                size="lg"
              >
                Guardar Cambios
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Modal para dibujar firma del director */}
      <Modal isOpen={isCanvasOpen} onClose={onCanvasClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Dibujar Firma del Director</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <SignatureCanvasComponent
              onSignatureSave={handleDirectorSignatureSave}
              onCancel={handleCanvasCancel}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InstitutionSettingsTab;
